import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  Plus, Trash, PencilSimple, ShieldCheck, Eye, EyeSlash,
  UserCheck, ChatsTeardrop, WhatsappLogo, ChatCircleDots
} from '@phosphor-icons/react';

// Exact permission options available in the platform
const PERMISSION_OPTIONS = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'reports', label: 'Reports & Analytics' },
  { key: 'admin_chat', label: 'Admin Chat' },
  { key: 'credits', label: 'Credits' },
  { key: 'live_chat', label: 'Live Chat (Telegram)' },
  { key: 'whatsapp_chat', label: 'WhatsApp Chat' },
  { key: 'assigned_chats', label: 'Assigned Chats Only' },
  { key: 'broadcast', label: 'Broadcast' },
  { key: 'channel_broadcast', label: 'Channel Broadcast' },
  { key: 'scheduled_broadcast', label: 'Scheduled Broadcast' },
  { key: 'broadcast_reports', label: 'Broadcast Reports' },
  { key: 'user_reports', label: 'User Reports' },
  { key: 'mobile_reports', label: 'Mobile Reports' },
  { key: 'edit_bots', label: 'Edit Bots' },
  { key: 'bot_settings', label: 'Bot Settings' },
  { key: 'bot_automation', label: 'Bot Automation' },
  { key: 'auto_messages', label: 'Auto Messages' },
  { key: 'delete_chat', label: 'Delete Chats (WhatsApp & Telegram)' },
];

export default function StaffPage() {
  const { user } = useAuth();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);

  // Add Dialog
  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState('');
  const [addEmail, setAddEmail] = useState('');
  const [addPassword, setAddPassword] = useState('');
  const [addPerms, setAddPerms] = useState([]);
  const [addClientData, setAddClientData] = useState(false);
  const [savingAdd, setSavingAdd] = useState(false);

  // Edit Basic Info Dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPassword, setEditPassword] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  // Dedicated Permissions Dialog (Same like Admin Management)
  const [permsDialogOpen, setPermsDialogOpen] = useState(false);
  const [permsStaff, setPermsStaff] = useState(null);
  const [permsList, setPermsList] = useState([]);
  const [permsClientData, setPermsClientData] = useState(false);
  const [savingPerms, setSavingPerms] = useState(false);

  // Check if current user (Admin or Super Admin) has client data visibility enabled
  const adminHasClientData = user?.role === 'super_admin' || user?.permissions?.includes('view_mobile') || user?.permissions?.includes('all') || user?.client_data_visibility === true;

  // Filter: ONLY show sections which Super Admin gave to this Admin
  const availablePerms = useMemo(() => {
    return PERMISSION_OPTIONS.filter(p => {
      if (user?.role === 'super_admin') return true;
      const adminPerms = user?.permissions || [];
      if (adminPerms.includes('all')) return true;

      // Allow assigned_chats if admin has live_chat
      if (p.key === 'assigned_chats') return adminPerms.includes('live_chat') || adminPerms.includes('assigned_chats');

      // Only show permissions which Super Admin gave to this Admin
      return adminPerms.includes(p.key);
    });
  }, [user]);

  const fetchStaff = () => {
    API.get('/staff').then(r => setStaff(r.data.staff || [])).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchStaff();
  }, []);

  // Open Add Dialog
  const openAddModal = () => {
    setAddName('');
    setAddEmail('');
    setAddPassword('');
    // Default chat permissions if available to admin
    const defaults = ['assigned_chats', 'live_chat', 'whatsapp_chat', 'admin_chat'].filter(k => 
      availablePerms.some(p => p.key === k)
    );
    setAddPerms(defaults);
    setAddClientData(false);
    setAddOpen(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!addName.trim() || !addEmail.trim() || !addPassword.trim()) return toast.error('Fill all fields');
    setSavingAdd(true);
    try {
      const perms = [...addPerms];
      const isVis = addClientData && adminHasClientData;
      if (isVis) {
        if (!perms.includes('view_mobile')) perms.push('view_mobile');
        if (!perms.includes('view_user_id')) perms.push('view_user_id');
      }

      await API.post('/staff', { 
        name: addName.trim(), 
        email: addEmail.trim().toLowerCase(), 
        password: addPassword, 
        permissions: perms,
        client_data_visibility: isVis,
        unmask_data: isVis
      });
      toast.success('Staff created successfully');
      setAddOpen(false);
      fetchStaff();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create staff');
    } finally {
      setSavingAdd(false);
    }
  };

  // Open Edit Basic Info Dialog
  const openEdit = (sa) => {
    setEditId(sa.id);
    setEditName(sa.name || '');
    setEditEmail(sa.email || '');
    setEditPassword('');
    setEditOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSavingEdit(true);
    const body = { 
      name: editName.trim(), 
      email: editEmail.trim().toLowerCase()
    };
    if (editPassword.trim()) body.password = editPassword;
    try {
      await API.put(`/staff/${editId}`, body);
      toast.success('Staff info updated');
      setEditOpen(false);
      fetchStaff();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update staff');
    } finally {
      setSavingEdit(false);
    }
  };

  // Open Dedicated Permissions Dialog (Same like screenshot)
  const openPermissions = (sa) => {
    setPermsStaff(sa);
    const existingPerms = sa.permissions || [];
    setPermsList([...existingPerms]);
    const isVis = sa.client_data_visibility === true || existingPerms.includes('view_mobile');
    setPermsClientData(isVis);
    setPermsDialogOpen(true);
  };

  const togglePermsClientData = () => {
    setPermsClientData(prev => !prev);
  };

  const handleSavePermissions = async () => {
    if (!permsStaff) return;
    setSavingPerms(true);
    try {
      let finalPerms = [...permsList];
      const isVis = permsClientData && adminHasClientData;
      if (isVis) {
        if (!finalPerms.includes('view_mobile')) finalPerms.push('view_mobile');
        if (!finalPerms.includes('view_user_id')) finalPerms.push('view_user_id');
      } else {
        finalPerms = finalPerms.filter(k => k !== 'view_mobile' && k !== 'view_user_id');
      }

      await API.put(`/staff/${permsStaff.id}`, {
        permissions: finalPerms,
        client_data_visibility: isVis,
        unmask_data: isVis
      });
      toast.success(`Permissions updated for ${permsStaff.name}`);
      setPermsDialogOpen(false);
      fetchStaff();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update permissions');
    } finally {
      setSavingPerms(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this staff member?')) return;
    try {
      await API.delete(`/staff/${id}`);
      toast.success('Staff deleted');
      fetchStaff();
    } catch (e) {
      toast.error('Failed to delete staff');
    }
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="staff-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>Staff Management</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage staff members and configure which sections are visible to them
          </p>
        </div>
        <Button onClick={openAddModal} className="gap-1.5 self-start sm:self-auto" data-testid="add-staff-btn">
          <Plus size={16} /> Add Staff
        </Button>
      </div>

      {loading ? (
        <p className="text-muted-foreground">Loading staff members...</p>
      ) : staff.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg p-12 text-center text-muted-foreground">
          No staff members yet. Click &ldquo;Add Staff&rdquo; to create one.
        </div>
      ) : (
        <div className="space-y-3">
          {staff.map(sa => {
            const perms = sa.permissions || [];
            const isVis = sa.client_data_visibility === true || perms.includes('view_mobile');

            // Find all matching permission labels for badges
            const activePermLabels = PERMISSION_OPTIONS.filter(p => perms.includes(p.key));

            return (
              <div 
                key={sa.id} 
                className="border border-border rounded-lg p-4 bg-card/60 hover:bg-card transition-colors flex flex-col gap-3" 
                data-testid={`staff-${sa.id}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-sm shrink-0 border border-primary/20">
                      {(sa.name || '?')[0].toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-sm truncate">{sa.name}</p>
                        <Badge variant={sa.is_active ? 'default' : 'destructive'} className="text-[10px] h-4.5 px-1.5">
                          {sa.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground truncate">{sa.email}</p>
                      {sa.admin_name && (
                        <p className="text-[11px] text-primary/80 font-medium mt-0.5">Admin: {sa.admin_name}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                    {/* Client Data Visibility Badge */}
                    <Badge variant={isVis ? 'secondary' : 'outline'} className={`text-[10px] gap-1 shrink-0 ${isVis ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20' : 'text-muted-foreground'}`}>
                      {isVis ? <Eye size={11} weight="bold" /> : <EyeSlash size={11} />}
                      Client Data: {isVis ? 'Visible' : 'Masked'}
                    </Badge>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-1.5 ml-auto sm:ml-0">
                      {/* Dedicated Permissions Button */}
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => openPermissions(sa)} 
                        className="h-8 px-2.5 text-xs gap-1.5 border-amber-500/30 text-amber-600 dark:text-amber-400 hover:bg-amber-500/10"
                        title="Permissions & Data Visibility"
                        data-testid={`perms-staff-${sa.id}`}
                      >
                        <ShieldCheck size={15} className="text-amber-500" />
                        <span>Permissions</span>
                      </Button>

                      {/* Edit Basic Info Button */}
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => openEdit(sa)} 
                        className="h-8 w-8 p-0"
                        title="Edit Info"
                        data-testid={`edit-staff-${sa.id}`}
                      >
                        <PencilSimple size={16} />
                      </Button>

                      {/* Delete Button */}
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleDelete(sa.id)} 
                        className="h-8 w-8 p-0 text-destructive hover:bg-destructive/10" 
                        title="Delete Staff"
                        data-testid={`del-staff-${sa.id}`}
                      >
                        <Trash size={16} />
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Visible Sections Badges */}
                <div className="pt-2 border-t border-border/50 flex flex-wrap items-center gap-1.5">
                  <span className="text-[11px] text-muted-foreground font-medium mr-1">Visible Sections:</span>
                  {activePermLabels.length === 0 ? (
                    <span className="text-[11px] text-muted-foreground italic">No sections visible</span>
                  ) : (
                    activePermLabels.map(p => (
                      <span 
                        key={p.key} 
                        className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-muted text-foreground border border-border/60"
                      >
                        {p.label}
                      </span>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ============================================================== */}
      {/* 1. DEDICATED PERMISSIONS DIALOG (EXACTLY LIKE ADMIN MANAGEMENT) */}
      {/* ============================================================== */}
      <Dialog open={permsDialogOpen} onOpenChange={setPermsDialogOpen}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck size={20} className="text-amber-500" />
              Permissions — {permsStaff?.name}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            {/* Client Data Visibility for Staff (if Admin has permission) */}
            {adminHasClientData && (
              <div className="border border-border/80 rounded-lg p-3 bg-muted/20 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ShieldCheck size={18} className="text-primary" />
                    <Label className="font-semibold text-sm">Client Data Visibility for Staff</Label>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    data-testid="toggle-perms-client-data-btn"
                    variant={permsClientData ? "default" : "outline"}
                    onClick={togglePermsClientData}
                    className={`h-7 px-3 text-xs font-semibold gap-1.5 transition-all ${permsClientData ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'text-muted-foreground'}`}
                  >
                    {permsClientData ? <Eye size={13} weight="bold" /> : <EyeSlash size={13} />}
                    {permsClientData ? 'Enabled' : 'Disabled'}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  {permsClientData
                    ? 'Enabled: This staff member can see unmasked client data (phone numbers and user IDs).'
                    : 'Disabled: Client data will be masked with asterisks.'}
                </p>
              </div>
            )}

            {/* Feature Permissions (Filtered to ONLY what Super Admin gave this Admin) */}
            <div className="space-y-2 border border-border/80 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <Label className="font-semibold text-sm">Feature Permissions</Label>
                <div className="flex items-center gap-2">
                  <button 
                    type="button" 
                    onClick={() => setPermsList(availablePerms.map(p => p.key))} 
                    className="text-[11px] text-primary hover:underline"
                  >
                    Select All
                  </button>
                  <span className="text-muted-foreground text-[11px]">•</span>
                  <button 
                    type="button" 
                    onClick={() => setPermsList([])} 
                    className="text-[11px] text-muted-foreground hover:underline"
                  >
                    Clear All
                  </button>
                </div>
              </div>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-1">
                {availablePerms.map(p => (
                  <label
                    key={p.key}
                    htmlFor={`perm-check-${p.key}`}
                    className="flex items-center gap-2 p-2 rounded-md hover:bg-muted/50 cursor-pointer border border-border/40 select-none"
                  >
                    <Checkbox
                      id={`perm-check-${p.key}`}
                      checked={permsList.includes(p.key)}
                      onCheckedChange={(checked) => {
                        setPermsList(prev => checked ? [...prev, p.key] : prev.filter(k => k !== p.key));
                      }}
                    />
                    <span className="text-xs cursor-pointer flex-1 truncate font-medium">
                      {p.label}
                    </span>
                  </label>
                ))}
              </div>

              {availablePerms.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-4">
                  No section permissions enabled on your Admin account.
                </p>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" type="button" onClick={() => setPermsDialogOpen(false)}>Cancel</Button>
              <Button type="button" onClick={handleSavePermissions} disabled={savingPerms}>
                {savingPerms ? 'Saving...' : 'Save Permissions'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* ============================================================== */}
      {/* 2. ADD STAFF DIALOG                                            */}
      {/* ============================================================== */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Staff Member</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input value={addName} onChange={e => setAddName(e.target.value)} placeholder="Staff Member Name" required data-testid="staff-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input type="email" value={addEmail} onChange={e => setAddEmail(e.target.value)} placeholder="staff@example.com" required data-testid="staff-email" />
            </div>
            <div className="space-y-1.5">
              <Label>Password</Label>
              <Input type="password" value={addPassword} onChange={e => setAddPassword(e.target.value)} placeholder="Min 6 characters" required minLength={6} data-testid="staff-password" />
            </div>

            {/* Client Data Visibility for Staff */}
            {adminHasClientData && (
              <div className="border border-border/80 rounded-lg p-3 bg-muted/20 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ShieldCheck size={18} className="text-primary" />
                    <Label className="font-semibold text-sm">Client Data Visibility for Staff</Label>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    data-testid="toggle-staff-client-data"
                    variant={addClientData ? "default" : "outline"}
                    onClick={() => setAddClientData(v => !v)}
                    className={`h-7 px-3 text-xs font-semibold gap-1.5 transition-all ${addClientData ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'text-muted-foreground'}`}
                  >
                    {addClientData ? <Eye size={13} weight="bold" /> : <EyeSlash size={13} />}
                    {addClientData ? 'Enabled' : 'Disabled'}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  {addClientData
                    ? 'Enabled: This staff member can view unmasked client mobile numbers and user IDs.'
                    : 'Disabled: Client phone numbers and user IDs will be masked with asterisks.'}
                </p>
              </div>
            )}

            {/* Feature Permissions */}
            <div className="space-y-2 border border-border/80 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <Label className="font-semibold text-sm">Feature Permissions</Label>
                <div className="flex items-center gap-2">
                  <button 
                    type="button" 
                    onClick={() => setAddPerms(availablePerms.map(p => p.key))} 
                    className="text-[11px] text-primary hover:underline"
                  >
                    Select All
                  </button>
                  <span className="text-muted-foreground text-[11px]">•</span>
                  <button 
                    type="button" 
                    onClick={() => setAddPerms([])} 
                    className="text-[11px] text-muted-foreground hover:underline"
                  >
                    Clear All
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-1">
                {availablePerms.map(p => (
                  <label
                    key={p.key}
                    htmlFor={`add-perm-${p.key}`}
                    className="flex items-center gap-2 p-2 rounded-md hover:bg-muted/50 cursor-pointer border border-border/40 select-none"
                  >
                    <Checkbox
                      id={`add-perm-${p.key}`}
                      checked={addPerms.includes(p.key)}
                      onCheckedChange={(checked) => {
                        setAddPerms(prev => checked ? [...prev, p.key] : prev.filter(k => k !== p.key));
                      }}
                    />
                    <span className="text-xs cursor-pointer flex-1 truncate font-medium">
                      {p.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" type="button" onClick={() => setAddOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={savingAdd}>{savingAdd ? 'Saving...' : 'Add Staff'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ============================================================== */}
      {/* 3. EDIT STAFF BASIC INFO DIALOG                                */}
      {/* ============================================================== */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Staff Info</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input value={editName} onChange={e => setEditName(e.target.value)} required />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input type="email" value={editEmail} onChange={e => setEditEmail(e.target.value)} required />
            </div>
            <div className="space-y-1.5">
              <Label>New Password (optional)</Label>
              <Input type="password" value={editPassword} onChange={e => setEditPassword(e.target.value)} minLength={6} placeholder="Leave blank to keep current" />
            </div>

            <DialogFooter>
              <Button variant="outline" type="button" onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={savingEdit}>{savingEdit ? 'Saving...' : 'Save Changes'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
